import * as React from 'react'

export interface UseUnsavedChangesDialogOptions {
  // Function to check if there are unsaved changes
  hasUnsavedChanges?: () => boolean
  // Original handlers that we'll wrap
  onClose?: () => void
  onActionChange?: (action: string) => void
  onGenerate?: () => Promise<void>
  // New options for tracking image changes
  currentImageUrl?: string
  onSaveImage?: () => void
  // Function to reset the modified flag when changes are discarded
  resetModifiedFlag?: () => void
}

export interface UseUnsavedChangesDialogResult {
  // Whether the dialog is currently shown
  isDialogOpen: boolean
  // Wrapped handlers that will check for unsaved changes
  handleClose: () => void
  handleActionChange: (action: string) => void
  handleGenerate: () => Promise<void>
  // Dialog control functions
  closeDialog: () => void
  // Dialog action handlers
  handleSaveChanges: () => void
  handleDiscardChanges: () => void
  // Current pending action
  pendingAction: 'close' | 'changeAction' | 'generate' | 'selectImage' | null
  pendingActionValue: string | null
  // Function to mark the current state as saved
  markAsSaved: () => void
  // Whether there are unsaved changes
  hasUnsavedChanges: boolean
  // Additional functions for external control
  setIsDialogOpen: (isOpen: boolean) => void
  setPendingAction: (action: 'close' | 'changeAction' | 'generate' | 'selectImage' | null) => void
}

export const useUnsavedChangesDialog = ({
  hasUnsavedChanges: externalHasUnsavedChanges,
  onClose,
  onActionChange,
  onGenerate,
  currentImageUrl,
  onSaveImage,
  resetModifiedFlag,
}: UseUnsavedChangesDialogOptions): UseUnsavedChangesDialogResult => {
  // State to track if the dialog is open
  const [isDialogOpen, setIsDialogOpen] = React.useState(false)

  // State to track the pending action (what the user was trying to do when the dialog was shown)
  const [pendingAction, setPendingAction] = React.useState<'close' | 'changeAction' | 'generate' | 'selectImage' | null>(null)
  const [pendingActionValue, setPendingActionValue] = React.useState<string | null>(null)

  // State to track the last saved image URL and whether there are unsaved changes
  const [lastSavedImageUrl, setLastSavedImageUrl] = React.useState<string | undefined>(currentImageUrl)
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false)

  // Update hasUnsavedChanges when the current image URL changes
  React.useEffect(() => {
    if (currentImageUrl !== lastSavedImageUrl) {
      setHasUnsavedChanges(true)
    }
  }, [currentImageUrl, lastSavedImageUrl])

  // Function to mark the current state as saved
  const markAsSaved = React.useCallback(() => {
    setLastSavedImageUrl(currentImageUrl)
    setHasUnsavedChanges(false)
  }, [currentImageUrl])

  // Function to check if there are unsaved changes
  const checkUnsavedChanges = React.useCallback(() => {
    // If an external function is provided, use it
    if (externalHasUnsavedChanges) {
      return externalHasUnsavedChanges()
    }
    // Otherwise, use our internal tracking
    return hasUnsavedChanges
  }, [externalHasUnsavedChanges, hasUnsavedChanges])

  // Function to close the dialog
  const closeDialog = React.useCallback(() => {
    setIsDialogOpen(false)
  }, [])

  // Wrapped handler for closing the modal
  const handleClose = React.useCallback(() => {
    if (checkUnsavedChanges()) {
      // If there are unsaved changes, show the dialog and set the pending action
      setIsDialogOpen(true)
      setPendingAction('close')
      setPendingActionValue(null)
    } else {
      // If no unsaved changes, just call the original onClose
      onClose?.()
    }
  }, [checkUnsavedChanges, onClose])

  // Wrapped handler for changing the action/tool
  const handleActionChange = React.useCallback((action: string) => {
    if (checkUnsavedChanges()) {
      // If there are unsaved changes, show the dialog and set the pending action
      setIsDialogOpen(true)
      setPendingAction('changeAction')
      setPendingActionValue(action)
    } else {
      // If no unsaved changes, just call the original onActionChange
      onActionChange?.(action)
    }
  }, [checkUnsavedChanges, onActionChange])

  // Wrapped handler for generating new images with AI
  const handleGenerate = React.useCallback(async () => {
    if (checkUnsavedChanges()) {
      // If there are unsaved changes, show the dialog and set the pending action
      setIsDialogOpen(true)
      setPendingAction('generate')
      setPendingActionValue(null)
      // Return a resolved promise to prevent errors in the calling code
      return Promise.resolve()
    } else {
      // If no unsaved changes, just call the original onGenerate
      return onGenerate?.() ?? Promise.resolve()
    }
  }, [checkUnsavedChanges, onGenerate])

  // Handler for when the user chooses to save changes
  const handleSaveChanges = React.useCallback(() => {
    // Close the dialog
    setIsDialogOpen(false)

    // Save the changes
    if (onSaveImage) {
      try {
        onSaveImage()
      } catch (error) {
        // Silent error handling in this example.
        // In production, consider proper error logging and user notification.
      }
    }

    // Mark as saved
    markAsSaved()

    // Execute the pending action
    if (pendingAction === 'close') {
      onClose?.()
    } else if (pendingAction === 'changeAction' && pendingActionValue) {
      onActionChange?.(pendingActionValue)
    } else if (pendingAction === 'generate') {
      onGenerate?.()
    }

    // Reset the pending action
    setPendingAction(null)
    setPendingActionValue(null)
  }, [pendingAction, pendingActionValue, onClose, onActionChange, onGenerate, onSaveImage, markAsSaved])

  // Handler for when the user chooses to discard changes
  const handleDiscardChanges = React.useCallback(() => {
    // Close the dialog
    setIsDialogOpen(false)

    // Mark as saved (to prevent the dialog from showing again)
    markAsSaved()

    // Call the resetModifiedFlag function if provided
    // This explicitly sets the modified flag to false in the parent component
    resetModifiedFlag?.()

    // Execute the pending action without saving
    if (pendingAction === 'close') {
      onClose?.()
    } else if (pendingAction === 'changeAction' && pendingActionValue) {
      onActionChange?.(pendingActionValue)
    } else if (pendingAction === 'generate') {
      onGenerate?.()
    }

    // Reset the pending action
    setPendingAction(null)
    setPendingActionValue(null)
  }, [pendingAction, pendingActionValue, onClose, onActionChange, onGenerate, markAsSaved, resetModifiedFlag])

  return {
    isDialogOpen,
    handleClose,
    handleActionChange,
    handleGenerate,
    closeDialog,
    handleSaveChanges,
    handleDiscardChanges,
    pendingAction,
    pendingActionValue,
    markAsSaved,
    hasUnsavedChanges,
    setIsDialogOpen,
    setPendingAction,
  }
}
